import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { healthsherpa, clients } from './api';
import ClientPicker from './ClientPicker';
import { PlanView, CompareView, CompareChooser } from './HealthSherpaPresent';   // 5.16.0
import CarrierLogos from './HealthSherpaLogos';                                           // 5.16.0
import PlanTable, { ROW_FONT_MIN, ROW_FONT_MAX, loadRowFont, saveRowFont, useColumnWidths } from './HealthSherpaTable';   // 5.18.0, 5.18.1
import { CHIP, rowTone } from './hsColors';                                              // 5.18.0
import { AlertsBox } from './HealthSherpaPolicy';                                       // 5.19.0 phase 2
import ClientsTab, { readHsUrl, writeHsUrl } from './HealthSherpaClients';                 // 5.20.0
import { AlertTypesCard, FinalizedTab, FollowupsTab, MyClientsTab, writeWorkUrl } from './HealthSherpaWork';   // 5.30.0
import { ROOT_CSS, TextSizeControl, fs, useHsTextScale } from './hsScale';   // 5.31.0: page-wide Text size (per user)
import EnrollDialog from './HealthSherpaEnroll';
import { CmsStatusCard, MissingDataCard, PiColumnsCard, PiViewLogCard } from './HsSettingsCards';   // 5.46.0
import { RenewalBonusTab } from './HsRenewalBonus';   // 5.47.0
import { PiCaptureResult } from './HsPersonalInfo';   // 5.48.0
import { ageOn } from './hsPersonal';                 // 5.48.0
import HsLockStrip, { HsLockLogCard } from './HsLockStrip';                          // 5.44.0 "HealthSherpa in use"                                   // 5.39.0: Start enrollment
import OpenEnrollmentTab from './HealthSherpaOE';                                 // 5.39.0: Open Enrollment worklist
import { LINK_ESTIMATE, smsSegments, smsText, templateProblem } from './hsSms';    // 5.39.0
import TodayCard from './TodayCard';                                              // 5.50.0 calendar notices (as on Main)

// =============================================================================
// HealthSherpa page (5.15.0, phase 1: ACA quoting). Full-screen, Renewals pattern.
// Tabs: Dashboard · New Quote (form + results) · Quotes · Clients (5.20.0; was "Policy status") · Settings (admin).
// 5.39.0: "Start enrollment" in the top bar (every tab) and on a quote's bottom row; the "Open Enrollment" tab
// (same visibility as Clients).
// 5.20.0: the open tab is in the address bar (&tab=…) and the Clients tab keeps its search / filters / person
// there too, so the browser's Back button works.
// Every HealthSherpa call goes through /api/healthsherpa (the API key never reaches the
// browser). Quotes are stored server-side; ticks auto-save; the quote PDF is saved into the
// linked client's HealthSherpa/<plan year>/ folder.
// =============================================================================

const detailOf = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return e?.message || 'Something went wrong';
};

const usd = (v) => (v === null || v === undefined || Number.isNaN(Number(v)))
  ? '—'
  : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// $0.00 net premiums always carry the estimate marker (never an unqualified "$0").
const netCell = (v) => (v !== null && v !== undefined && Math.abs(Number(v)) < 0.005) ? `${usd(v)}*` : usd(v);

const amount = (v, text) => (v !== null && v !== undefined) ? usd(v) : (text || '—');

const pretty = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—');

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' });
};

// 5.16.0: start dates come from GET /api/healthsherpa/enrollment-dates — never computed here.

// Last 5-digit number in the client's one-line address ("… HOUSTON TX 77083" -> "77083").
const zipFromAddress = (a) => {
  const all = String(a || '').match(/\b\d{5}\b/g);
  return all && all.length ? all[all.length - 1] : '';
};

// 5.18.0 results summary helpers
const fmtShortDate = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
const countyLabel = (name) => (!name ? '' : / county$/i.test(name) ? ` ${name}` : ` ${name} County`);
// Calm outlined chip (quote type, cached) — muted, never a loud fill.
const chip = (c) => ({ fontSize: fs(12), color: CHIP[c.darkMode ? 'dark' : 'light'], border: `1px solid ${c.borderColor}`, borderRadius: 10, padding: '1px 8px', whiteSpace: 'nowrap' });

const SORTS = [
  { key: 'net_premium', field: 'net_premium', label: 'lowest estimated net premium' },
  { key: 'gross_premium', field: 'gross_premium', label: 'lowest monthly premium before tax credit' },
  { key: 'deductible', field: 'deductible_individual', label: 'lowest individual deductible' },
  { key: 'moop', field: 'moop_individual', label: 'lowest individual out-of-pocket maximum' },
];

const SETTING_LABELS = [
  ['agency_name', 'Agency name (PDF header)'],
  ['agency_phone', 'Agency phone (PDF header, optional)'],
  ['agent_name', 'Agent name (PDF header, optional)'],
  ['agent_npn', 'Agent NPN'],
  ['web_broker_name', 'Web broker name (PDF footer)'],
  ['web_broker_id', 'Web broker ID (PDF footer, optional)'],
  ['folder_top_name', 'Client folder name for quote PDFs'],
  ['quote_retention_days', 'Delete unlinked quotes after N days (empty = keep forever)'],
  ['oe_start', 'Open enrollment starts (MM-DD, every year)'],
  ['oe_jan1_deadline', 'Enroll by this day for a Jan 1 start, else Feb 1 (MM-DD)'],
  ['oe_end', 'Open enrollment ends (MM-DD; may be in the new year)'],
  ['agency_npn', 'Agency NPN'],
  ['status_tab_staff_visible', 'Show HealthSherpa Clients to staff (Clients tab, client Health coverage, alerts)'],
  ['enroll_sms_template', 'Self-service text message (Start enrollment → Text link to client)'],   // 5.39.0
  ['export_stale_days', 'Flag a client\'s export data as stale after N days (My Clients, client view)'],   // 5.46.0
];

// 5.28.1 (DT 1.4.2): enrollments and agent-of-record checks use the AGENT's NPN; the agency NPN is reference only.
const SETTING_HINTS = {
  agent_npn: 'Your individual NPN — used for enrollments, the quote PDF, and agent-of-record checks.',
  agency_npn: 'Agency business NPN — reference only; not used for enrollments or agent-of-record checks.',
};

// 5.48.0: + dob (optional — sets the age; saved to an existing client's personal info, never sent to HealthSherpa),
// member_key / label (who it is, from the client's household), touched (what the user typed / changed — only that is
// saved), carried (filled from LAST year's stored values — a default, never saved unless changed)
const emptyPerson = (relationship) => ({ relationship, age: '', uses_tobacco: false, pregnant: false, dob: '', member_key: null, label: '', touched: [], carried: [] });

function useColors(darkMode) {
  return {
    bg: darkMode ? '#1a1a1a' : '#f5f5f5',
    panelBg: darkMode ? '#242424' : '#fff',
    subPanelBg: darkMode ? '#1a1a1a' : '#f5f5f5',
    borderColor: darkMode ? '#333' : '#e5e5e5',
    textPrimary: darkMode ? '#f0f0f0' : '#1a1a1a',
    textSecondary: darkMode ? '#aaa' : '#666',
    inputBg: darkMode ? '#1a1a1a' : '#fff',
    darkMode,
  };
}

function ErrorBox({ msg }) {
  if (!msg) return null;
  return <div role="alert" style={{ background: '#A32D2D', color: '#fff', padding: '8px 12px', borderRadius: 5, fontSize: fs(13), margin: '8px 0' }}>{msg}</div>;
}

function Card({ c, title, children, muted }) {
  return (
    <div style={{ background: c.panelBg, border: `1px solid ${c.borderColor}`, borderRadius: 8, padding: 16, opacity: muted ? 0.55 : 1 }}>
      {title && <div style={{ fontSize: fs(13), fontWeight: 700, marginBottom: 10, color: c.textPrimary }}>{title}</div>}
      {children}
    </div>
  );
}

const btn = (c, primary, disabled) => ({
  padding: '7px 14px', fontSize: fs(13), borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer',
  background: primary ? '#185FA5' : c.panelBg, color: primary ? '#fff' : c.textPrimary,
  border: primary ? 'none' : `1px solid ${c.borderColor}`, opacity: disabled ? 0.5 : 1, fontWeight: primary ? 600 : 400,
});

const input = (c, width) => ({
  padding: '8px 10px', fontSize: fs(14), background: c.inputBg, color: c.textPrimary,
  border: `1px solid ${c.borderColor}`, borderRadius: 5, boxSizing: 'border-box', width: width || '100%',
});

const label = (c) => ({ fontSize: fs(12), color: c.textSecondary, marginBottom: 4 });

// 5.18.1: results-area controls sized in em, so the text-size slider scales them with the table.
const toolInput = (c) => ({ ...input(c, 'auto'), fontSize: '0.9em', fontFamily: 'inherit', padding: '0.35em 0.5em', borderRadius: '0.3em', maxWidth: '15em' });
const toolChip = (c) => ({ ...chip(c), fontSize: '1em', borderRadius: '0.8em', padding: '0.05em 0.6em' });

// --- Dashboard ---------------------------------------------------------------------------------

function Dashboard({ c, isAdmin, onOpenQuote, onOpenClient, onOpenPerson, onGoPolicy }) {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    healthsherpa.summary().then((r) => setSummary(r.data)).catch((e) => setError(detailOf(e)));
    healthsherpa.listQuotes({ limit: 10 }).then((r) => setRecent(r.data.quotes || [])).catch(() => {});
    if (isAdmin) healthsherpa.status().then((r) => setStatus(r.data)).catch(() => {});
  }, [isAdmin]);

  return (
    <div style={{ padding: 18, display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', alignContent: 'start' }}>
      <ErrorBox msg={error} />
      <Card c={c} title="Quotes this month">
        <div style={{ fontSize: fs(28), fontWeight: 700 }}>{summary ? summary.quotes_this_month : '…'}</div>
        <div style={{ fontSize: fs(12), color: c.textSecondary }}>all users · mine: {summary ? summary.my_quotes_this_month : '…'}</div>
      </Card>
      {isAdmin && (
        <Card c={c} title="HealthSherpa API (admin)">
          {status ? (
            <div style={{ fontSize: fs(13), lineHeight: 1.7 }}>
              <div style={{ color: status.usage_level === 'red' ? '#e74c3c' : status.usage_level === 'amber' ? '#e67e22' : 'inherit', fontWeight: status.usage_level === 'ok' ? 400 : 700 }}>
                Calls this month: <strong>{status.calls_this_month.toLocaleString()}</strong> of {status.monthly_limit.toLocaleString()}
                {status.usage_level !== 'ok' && ` — ${status.usage_level === 'red' ? 'very close to' : 'nearing'} the monthly limit`}
              </div>
              <div>Last call: {status.last_call_at ? `${fmtDate(status.last_call_at)} (${status.last_call_status ?? 'no answer'})` : 'none yet'}</div>
              <div>API key: {status.key_configured ? '✓ configured' : '✗ not configured'}</div>
            </div>
          ) : <div style={{ fontSize: fs(12), color: c.textSecondary }}>Loading…</div>}
        </Card>
      )}
      {/* 5.19.0 phase 2: one concise alerts box (staff see it only when the admin switch is on) */}
      <div style={{ gridColumn: '1 / -1' }}>
        <AlertsBox c={c} onOpenClient={onOpenClient} onOpenPerson={onOpenPerson} onNeedsLinking={onGoPolicy} />
      </div>
      <div style={{ gridColumn: '1 / -1' }}>
        <Card c={c} title="Recent quotes">
          {recent.length === 0 ? <div style={{ fontSize: fs(12), color: c.textSecondary }}>No quotes yet.</div> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fs(13) }}>
              <thead><tr style={{ textAlign: 'left', color: c.textSecondary }}>
                <th style={{ padding: 6 }}>Date</th><th style={{ padding: 6 }}>Client</th><th style={{ padding: 6 }}>Plan year</th>
                <th style={{ padding: 6 }}>ZIP</th><th style={{ padding: 6 }}>Ticked</th><th style={{ padding: 6 }}>PDF</th><th />
              </tr></thead>
              <tbody>{recent.map((q) => (
                <tr key={q.id} style={{ borderTop: `1px solid ${c.borderColor}` }}>
                  <td style={{ padding: 6 }}>{fmtDate(q.created_at)}</td>
                  <td style={{ padding: 6 }}>{q.client_name || <span style={{ color: c.textSecondary }}>Not linked</span>}</td>
                  <td style={{ padding: 6 }}>{q.plan_year}</td>
                  <td style={{ padding: 6 }}>{q.zip}</td>
                  <td style={{ padding: 6 }}>{q.selected_count}</td>
                  <td style={{ padding: 6 }}>{q.last_pdf_file_id ? '✓' : ''}</td>
                  <td style={{ padding: 6, textAlign: 'right' }}><button style={btn(c)} onClick={() => onOpenQuote(q.id)}>Open</button></td>
                </tr>))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}

// --- New client (same duplicate-check behaviour as the main + New Client modal) ---------------------------

function NewClientForm({ c, onCreated, onPickExisting, onCancel, initial }) {
  // 5.19.0: `initial` prefills it from a HealthSherpa export row (5.20.0: Clients -> Assign -> + New client)
  const [f, setF] = useState(() => ({ name: '', address_name: '', phone1: '', email: '', ...(initial || {}) }));
  const [dupes, setDupes] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: k === 'name' ? v.toUpperCase() : v })); if (k !== 'address_name') setDupes(null); };
  const ok = f.name.trim() && f.address_name.trim();

  const create = async (skipCheck) => {
    if (!ok) return;
    setSaving(true); setError(null);
    if (!skipCheck) {
      try {
        const d = await clients.possibleDuplicates({ name: f.name.trim(), phone: f.phone1.trim(), email: f.email.trim() });
        const found = d.data?.results || [];
        if (found.length) { setDupes(found); setSaving(false); return; }
      } catch (_e) { /* check unavailable: create as before */ }
    }
    try {
      const r = await clients.createNew({ name: f.name.trim(), address_name: f.address_name.trim(), phone1: f.phone1.trim(), email: f.email.trim(), source: 'healthsherpa' });
      onCreated({ ...r.data.client, address_name: f.address_name.trim() });
    } catch (e) {
      setError(detailOf(e));
    }
    setSaving(false);
  };

  return (
    <div style={{ border: `1px solid ${c.borderColor}`, borderRadius: 6, padding: 12, marginTop: 8 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <div><div style={label(c)}>Client name *</div><input style={input(c)} value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="e.g. JOHN SMITH" /></div>
        <div><div style={label(c)}>Address *</div><input style={input(c)} value={f.address_name} onChange={(e) => set('address_name', e.target.value)} placeholder="e.g. 16726 MISSIONARY RIDGE LN HOUSTON TX 77083" /></div>
        <div><div style={label(c)}>Phone</div><input style={input(c)} value={f.phone1} onChange={(e) => set('phone1', e.target.value)} /></div>
        <div><div style={label(c)}>Email</div><input style={input(c)} value={f.email} onChange={(e) => set('email', e.target.value)} /></div>
      </div>
      {dupes && dupes.length > 0 && (
        <div style={{ marginTop: 10, padding: '10px 12px', border: '1px solid #e67e22', borderRadius: 6, background: c.darkMode ? '#2a2117' : '#fff7ed' }}>
          <div style={{ fontSize: fs(12), fontWeight: 600, color: '#e67e22', marginBottom: 6 }}>
            ⚠ {dupes.length === 1 ? 'A client' : `${dupes.length} clients`} may already exist — check before creating:
          </div>
          {dupes.map((d) => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderTop: `1px solid ${c.borderColor}` }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: fs(13), fontWeight: 600 }}>{d.name}</div>
                <div style={{ fontSize: fs(11), color: c.textSecondary }}>{[d.address_name, d.phone1, d.email].filter(Boolean).join(' · ') || '—'}</div>
                <div style={{ fontSize: fs(10), color: '#e67e22' }}>same {(d.matched_on || []).join(' + ')}</div>
              </div>
              <button style={btn(c)} onClick={() => onPickExisting(d)}>Open this client</button>
            </div>
          ))}
        </div>
      )}
      <ErrorBox msg={error} />
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button style={btn(c)} onClick={onCancel}>Cancel</button>
        <button style={{ ...btn(c, true, !ok || saving), background: dupes && dupes.length ? '#e67e22' : '#2ecc71' }} disabled={!ok || saving}
          onClick={() => create(!!(dupes && dupes.length))}>
          {saving ? 'Creating…' : (dupes && dupes.length) ? 'Create anyway' : 'Create client'}
        </button>
      </div>
    </div>
  );
}

// --- Client box ------------------------------------------------------------------------------

function ClientBox({ c, client, onSetClient, onClear, onClientsChanged }) {
  const [mode, setMode] = useState(null);        // null | 'pick' | 'new'
  return (
    <Card c={c} title="Client (optional until you save the PDF)">
      {client ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{client.name}</div>
            {client.address_name && <div style={{ fontSize: fs(12), color: c.textSecondary }}>{client.address_name}</div>}
          </div>
          <button style={btn(c)} onClick={() => setMode('pick')}>Change</button>
          <button style={btn(c)} onClick={onClear}>Unlink</button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={btn(c)} onClick={() => setMode(mode === 'pick' ? null : 'pick')}>Pick a client</button>
          <button style={btn(c)} onClick={() => setMode(mode === 'new' ? null : 'new')}>+ New client</button>
        </div>
      )}
      {mode === 'pick' && (
        <div style={{ marginTop: 10 }}>
          <ClientPicker darkMode={c.darkMode} onPick={(r) => { setMode(null); onSetClient(r); }} />
        </div>
      )}
      {mode === 'new' && (
        <NewClientForm c={c} onCancel={() => setMode(null)}
          onPickExisting={(d) => { setMode(null); onSetClient(d); }}
          onCreated={(nc) => { setMode(null); onSetClient(nc); if (onClientsChanged) onClientsChanged([nc.id]); }} />
      )}
    </Card>
  );
}

// --- Plan details drawer ---------------------------------------------------------------------

function DetailsDrawer({ c, plan, onClose }) {
  if (!plan) return null;
  const rows = [
    ['Carrier', plan.issuer_name], ['Plan', plan.name], ['Plan ID', plan.plan_id], ['Metal level', pretty(plan.metal_level)],
    ['Plan type', plan.plan_type], ['Network', [plan.network_name, plan.network_type].filter(Boolean).join(' · ')],
    ['HSA eligible', plan.hsa_eligible === true ? 'Yes' : plan.hsa_eligible === false ? 'No' : '—'],
    ['Standardized plan', plan.is_standardized === true ? 'Yes' : plan.is_standardized === false ? 'No' : '—'],
    ['Cost-sharing reduction', plan.csr_level ? pretty(plan.csr_level) : '—'],
    ['Monthly premium', usd(plan.gross_premium)], ['Estimated tax credit*', usd(plan.subsidy_applied)],
    ['Estimated net premium*', netCell(plan.net_premium)], ['Maximum tax credit*', usd(plan.max_aptc)],
    ['Deductible (individual / family)', `${amount(plan.deductible_individual, plan.deductible_individual_text)} / ${amount(plan.deductible_family, plan.deductible_family_text)}`],
    ['Out-of-pocket max (individual / family)', `${amount(plan.moop_individual, plan.moop_individual_text)} / ${amount(plan.moop_family, plan.moop_family_text)}`],
    ['Primary care', plan.primary_care], ['Specialist', plan.specialist], ['Urgent care', plan.urgent_care], ['Generic drugs', plan.generic_rx],
    ['Carrier phone', plan.issuer_phone],
  ];
  const docs = plan.documents || {};
  const docLabels = { summary_of_benefits: 'Summary of benefits', brochure: 'Brochure', formulary: 'Drug list', provider_network: 'Provider network', payment: 'Payment' };
  return (
    <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 460, maxWidth: '95vw', background: c.panelBg, borderLeft: `1px solid ${c.borderColor}`, zIndex: 950, boxShadow: '-8px 0 24px rgba(0,0,0,0.3)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: `1px solid ${c.borderColor}` }}>
        <div style={{ flex: 1, fontWeight: 700 }}>Plan details</div>
        <button style={btn(c)} onClick={onClose}>Close</button>
      </div>
      <div style={{ overflowY: 'auto', padding: 16, fontSize: fs(13) }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: 10, padding: '5px 0', borderBottom: `1px solid ${c.borderColor}` }}>
            <div style={{ width: 170, color: c.textSecondary, flexShrink: 0 }}>{k}</div><div>{v || '—'}</div>
          </div>
        ))}
        <div style={{ marginTop: 12, fontWeight: 600 }}>Documents</div>
        {Object.keys(docs).length === 0 ? <div style={{ color: c.textSecondary }}>None provided.</div> :
          Object.entries(docs).map(([k, u]) => (
            <div key={k} style={{ padding: '4px 0' }}><a href={u} target="_blank" rel="noopener noreferrer" style={{ color: '#4a9eda' }}>{docLabels[k] || pretty(k)} ↗</a></div>
          ))}
        <div style={{ marginTop: 12, fontSize: fs(11), color: c.textSecondary }}>* Estimates based on the information provided; actual cost depends on Marketplace eligibility.</div>
      </div>
    </div>
  );
}

// --- Results -----------------------------------------------------------------------------------
// (exported only for the headless proof harness; the page uses it internally)

export function Results({ c, quote, setQuote, openClientFolder, onClientTreesChanged, footerEl, onStartEnrollment }) {
  const [present, setPresent] = useState(null);     // index into `shown` (5.16.0 large plan view)
  const [rowFont, setRowFontState] = useState(loadRowFont);   // 5.18.0 text-size slider (remembered per browser)
  const setRowFont = (v) => { setRowFontState(v); saveRowFont(v); };
  const cols = useColumnWidths();                              // 5.18.1 resizable columns (em, remembered per browser)
  const [comparePlans, setComparePlans] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const [metal, setMetal] = useState('');
  const [ptype, setPtype] = useState('');
  const [carrier, setCarrier] = useState('');
  const [hsaOnly, setHsaOnly] = useState(false);
  const [sortKey, setSortKey] = useState('net_premium');
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(null);
  const [openedMsg, setOpenedMsg] = useState(null);     // 5.17.0: where "Open client folder" went
  const [error, setError] = useState(null);

  useEffect(() => { setSaved(null); setError(null); setOpenedMsg(null); }, [quote.id]);

  const plans = useMemo(() => quote.plans || [], [quote.plans]);
  const opts = (k) => Array.from(new Set(plans.map((p) => p[k]).filter(Boolean))).sort();
  const sort = SORTS.find((s) => s.key === sortKey) || SORTS[0];
  const shown = useMemo(() => {
    const list = plans.filter((p) => (!metal || p.metal_level === metal) && (!ptype || p.plan_type === ptype)
      && (!carrier || p.issuer_name === carrier) && (!hsaOnly || p.hsa_eligible === true));
    const val = (p) => (p[sort.field] === null || p[sort.field] === undefined ? Infinity : Number(p[sort.field]));
    return [...list].sort((a, b) => val(a) - val(b));
  }, [plans, metal, ptype, carrier, hsaOnly, sort.field]);
  const selected = quote.selected_plan_ids || [];

  const toggle = async (pid) => {
    const next = selected.includes(pid) ? selected.filter((x) => x !== pid) : [...selected, pid];
    setQuote({ ...quote, selected_plan_ids: next });
    try { await healthsherpa.setSelection(quote.id, next); } catch (e) { setError(detailOf(e)); }
  };

  const savePdf = async () => {
    setSaving(true); setError(null); setSaved(null);
    try {
      const sortedIds = [...plans].sort((a, b) => {
        const v = (p) => (p[sort.field] === null || p[sort.field] === undefined ? Infinity : Number(p[sort.field]));
        return v(a) - v(b);
      }).map((p) => p.plan_id);
      const r = await healthsherpa.savePdf(quote.id, { order: sortedIds.filter((id) => selected.includes(id)), sort: sort.key });
      setSaved(r.data);
      setQuote({ ...quote, last_pdf_file_id: r.data.file_id, last_pdf_filename: r.data.filename });
      if (onClientTreesChanged) onClientTreesChanged([r.data.client_id]);
    } catch (e) {
      setError(detailOf(e));
    }
    setSaving(false);
  };

  const canSave = !!quote.client_id && selected.length > 0 && !saving;
  // 5.16.2: the big Compare button lives only in the page's bottom row
  const openCompare = () => {
    const ticked = plans.filter((p) => selected.includes(p.plan_id));
    if (ticked.length > 4) setChoosing(true); else if (ticked.length >= 2) setComparePlans(ticked);
  };
  const compareButton = () => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <button data-testid="compare-plans" disabled={selected.length < 2} onClick={openCompare}
        title={selected.length < 2 ? 'Tick 2–4 plans to compare' : selected.length > 4 ? 'Choose 4 of the ticked plans to compare' : 'Open the large side-by-side comparison'}
        style={{ padding: '10px 20px', fontSize: fs(16), fontWeight: 700, borderRadius: 6, border: 'none', cursor: selected.length < 2 ? 'not-allowed' : 'pointer',
          background: selected.length < 2 ? (c.darkMode ? '#3a3a3a' : '#cfd6df') : '#e67e22', color: selected.length < 2 ? c.textSecondary : '#fff' }}>
        ⇆ Compare plans ({selected.length})
      </button>
      {selected.length < 2 && <span style={{ fontSize: fs(12), color: c.textSecondary }}>Tick 2–4 plans to compare</span>}
    </span>
  );
  const subtext = rowTone('subtext', c.darkMode);
  const sel = (v, set, options, all) => (
    <select value={v} onChange={(e) => set(e.target.value)} style={toolInput(c)}>
      <option value="">{all}</option>
      {options.map((o) => <option key={o} value={o}>{pretty(o)}</option>)}
    </select>
  );

  return (
    <div style={{ marginTop: 14 }}>
      <Card c={c}>
        {/* 5.18.1: ONE text size for the whole results area (the slider): toolbar, summary, headers, rows, badges,
            logos and buttons are all in em. The filter row sits directly on the table (same left edge and padding);
            summary + text size + Reset columns on the right of the same row. */}
        <div data-testid="hs-results-area" style={{ fontSize: fs(rowFont), fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", color: c.textPrimary }}>
          <div data-testid="hs-filter-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em 1.1em', alignItems: 'center', justifyContent: 'space-between', padding: '0 0.55em 0.45em' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5em', alignItems: 'center', flex: '0 1 auto' }}>
              {sel(metal, setMetal, opts('metal_level'), 'All metals')}
              {sel(ptype, setPtype, opts('plan_type'), 'All plan types')}
              {sel(carrier, setCarrier, opts('issuer_name'), 'All carriers')}
              <label style={{ fontSize: '0.9em', display: 'inline-flex', alignItems: 'center', gap: '0.35em', whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={hsaOnly} onChange={(e) => setHsaOnly(e.target.checked)} style={{ width: '1em', height: '1em', margin: 0 }} /> HSA only
              </label>
              <select value={sortKey} onChange={(e) => setSortKey(e.target.value)} style={toolInput(c)}>
                {SORTS.map((s) => <option key={s.key} value={s.key}>Sort: {s.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4em 0.8em', alignItems: 'center', justifyContent: 'flex-end', fontSize: '0.88em', color: subtext, flex: '1 1 30em', minWidth: 0 }}>
              <span style={{ flex: '1 1 16em', textAlign: 'right' }}>
                <strong style={{ color: c.textPrimary }}>{quote.result_count} plans</strong>
                {` · Plan year ${quote.plan_year} · starts ${fmtShortDate(quote.effective_date)} · ${quote.zip}${countyLabel(quote.county_name)} · Sorted by ${sort.label}`}
                {shown.length !== plans.length ? ` · showing ${shown.length}` : ''}
              </span>
              {quote.quote_type_label && (
                <span style={toolChip(c)}>{quote.quote_type_label}{quote.sep_reason_label ? ` — ${quote.sep_reason_label}` : ''}</span>
              )}
              {quote.from_cache && <span title="Same request within 15 minutes — HealthSherpa was not called again" style={toolChip(c)}>cached result</span>}
              <label title="Text size of the plan list" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4em' }}>
                <span style={{ fontSize: '0.9em' }} aria-hidden>A</span>
                <input type="range" min={ROW_FONT_MIN} max={ROW_FONT_MAX} step={1} value={rowFont} aria-label="Plan list text size" data-testid="hs-text-size"
                  onChange={(e) => setRowFont(Number(e.target.value))} style={{ width: '7.5em', margin: 0 }} />
                <span style={{ fontSize: '1.4em', lineHeight: 1 }} aria-hidden>A</span>
              </label>
              <button data-testid="hs-reset-columns" onClick={cols.reset} disabled={cols.isDefault}
                title={cols.isDefault ? 'The columns are at their default widths' : 'Put every column back to its default width'}
                style={{ font: 'inherit', padding: '0.25em 0.65em', borderRadius: '0.3em', border: `1px solid ${c.borderColor}`, background: 'transparent',
                  color: cols.isDefault ? subtext : c.textPrimary, cursor: cols.isDefault ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
                Reset columns
              </button>
            </div>
          </div>
          <ErrorBox msg={error} />
          <PlanTable plans={shown} selected={selected} onToggle={toggle} onPresent={(i) => setPresent(i)} onDetails={(p) => setDetail(p)}
            darkMode={c.darkMode} fontPx={rowFont} widths={cols.widths} setWidth={cols.setWidth} persistWidths={cols.persist} />
          <div style={{ fontSize: '0.85em', color: subtext, margin: '0.5em 0.55em 0' }}>* Estimates based on the information provided; actual cost depends on Marketplace eligibility. This is a quote, not an enrollment.</div>
        </div>
      </Card>
      {/* 5.16.2: ONE full-width row pinned to the bottom of the HealthSherpa window (rendered into the page's
          footer slot, outside the scrolling area, so the plan list never scrolls under it) */}
      {footerEl && createPortal(
      <div data-testid="hs-bottom-row" style={{ background: c.panelBg, borderTop: `1px solid ${c.borderColor}`, padding: '10px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <strong>{selected.length} plan{selected.length === 1 ? '' : 's'} ticked</strong>
        {compareButton()}
        {!quote.client_id && <span style={{ fontSize: fs(12), color: c.textSecondary }}>Link a client to save the PDF.</span>}
        {saved && (
          <span style={{ fontSize: fs(13) }}>Saved <strong>{saved.filename}</strong> ·{' '}
            <button style={{ ...btn(c), padding: '3px 8px' }} onClick={async () => {
              if (!openClientFolder) return;
              const where = await openClientFolder(saved.client_id);
              setOpenedMsg(where === 'tab' ? 'Opened in your InsureDocs tab — switch to it' : where === 'opened' ? 'Opened InsureDocs in another tab' : null);
            }}>Open client folder</button>
            {openedMsg && <span style={{ marginLeft: 8, color: c.textSecondary }}>{openedMsg}</span>}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {onStartEnrollment && (
          <button style={btn(c)} onClick={() => onStartEnrollment({ kind: 'quote', id: quote.id })} data-testid="hs-quote-enroll"
            title="Start a HealthSherpa enrollment prefilled from this quote">Start enrollment</button>
        )}
        <button style={btn(c, true, !canSave)} disabled={!canSave} onClick={savePdf}>{saving ? 'Saving…' : 'Save quote PDF to client'}</button>
      </div>, footerEl)}
      <DetailsDrawer c={c} plan={detail} onClose={() => setDetail(null)} />
      {present !== null && shown.length > 0 && (
        <PlanView plans={shown} index={Math.min(present, shown.length - 1)} setIndex={setPresent} onClose={() => setPresent(null)}
          selectedIds={selected} onToggle={toggle} darkMode={c.darkMode} />
      )}
      {choosing && (
        <CompareChooser plans={plans.filter((p) => selected.includes(p.plan_id))} darkMode={c.darkMode}
          onClose={() => setChoosing(false)} onCompare={(ps) => { setChoosing(false); setComparePlans(ps); }} />
      )}
      {comparePlans && <CompareView plans={comparePlans} onClose={() => setComparePlans(null)} darkMode={c.darkMode} />}
    </div>
  );
}

// --- New quote (form + results) ----------------------------------------------------------------

function NewQuote({ c, form, setForm, quote, setQuote, client, setClient, openClientFolder, onClientTreesChanged, onClientsChanged, footerEl, onStartEnrollment,
  quotePerson }) {
  const [counties, setCounties] = useState([]);
  const [countyMsg, setCountyMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [dates, setDates] = useState(null);           // 5.16.0: from the backend, never computed here
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadDates = () => healthsherpa.enrollmentDates().then((r) => {
    setDates(r.data);
    setForm((f) => {
      const allowed = r.data.oe_open ? ['open_enrollment', 'special_enrollment'] : ['special_enrollment'];
      return allowed.includes(f.quote_type) ? f : { ...f, quote_type: r.data.default_quote_type, sep_reason: '' };
    });
  }).catch((e) => setError(detailOf(e)));
  useEffect(() => { loadDates(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const startInfo = dates && form.quote_type ? (form.quote_type === 'open_enrollment' ? dates.open_enrollment : dates.special_enrollment) : null;
  // 5.46.0: "Start quote" from a HealthSherpa person — ages on the coverage start date + tobacco from the stored personal
  // info (the server logs the use); every field stays editable. Ages only: the quote never keeps a date of birth.
  const [prefillNote, setPrefillNote] = useState(null);
  const [prefilledFor, setPrefilledFor] = useState(null);
  useEffect(() => {
    if (!quotePerson || !startInfo || quote || prefilledFor === quotePerson) return undefined;
    let live = true;
    setPrefilledFor(quotePerson);
    const day = new Date(`${startInfo.effective_date}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    healthsherpa.coverage.quotePrefill(quotePerson, startInfo.effective_date).then((r) => {
      if (!live) return;
      if ((r.data.applicants || []).length) {
        setForm((f) => ({ ...f, applicants: r.data.applicants.map((a) => ({ ...emptyPerson(a.relationship), ...a, touched: [] })),
          annual_income: f.annual_income || (r.data.annual_income ? String(Math.round(Number(r.data.annual_income))) : ''),
          income_touched: false, income_last_year: !!(r.data.annual_income && r.data.income_last_year && !f.annual_income) }));
        setPrefillNote(r.data.used_stored ? `Ages (on ${day}) and tobacco filled from the client's stored personal info — check and edit as needed.`
          : 'Household filled from the latest HealthSherpa export — no dates of birth on file yet, enter the ages.');
      }
    }).catch((e) => { if (live) setError(detailOf(e)); });
    return () => { live = false; };
  }, [quotePerson, startInfo && startInfo.effective_date]);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!/^\d{5}$/.test(form.zip || '')) { setCounties([]); setCountyMsg(null); return; }
    let live = true;
    healthsherpa.counties(form.zip).then((r) => {
      if (!live) return;
      const list = r.data.counties || [];
      setCounties(list);
      setCountyMsg(list.length ? null : 'No counties found for that ZIP.');
      setForm((f) => {
        if (list.some((x) => x.fips_code === f.fips)) return f;
        const one = list.length === 1 ? list[0] : null;
        return { ...f, fips: one ? one.fips_code : '', state: one ? one.state : '', county_name: one ? one.name : '' };
      });
    }).catch((e) => { if (live) { setCounties([]); setCountyMsg(detailOf(e)); } });
    return () => { live = false; };
  }, [form.zip, setForm]);

  const setPerson = (i, k, v) => setForm((f) => ({ ...f, applicants: f.applicants.map((p, j) => {
    if (j !== i) return p;
    const touched = Array.from(new Set([...(p.touched || []), k]));
    const next = { ...p, [k]: v, touched, carried: (p.carried || []).filter((x) => x !== k) };
    if (k === 'dob') {                                   // 5.48.0: a date of birth sets the age on the start date
      const a = v && startInfo ? ageOn(v, startInfo.effective_date) : null;
      if (a !== null) next.age = String(a);
    }
    return next;
  }) }));
  const [piResult, setPiResult] = useState(null);       // 5.48.0: what the quote saved to the client's personal info
  const addPerson = (rel) => setForm((f) => ({ ...f, applicants: [...f.applicants, emptyPerson(rel)] }));
  const removePerson = (i) => setForm((f) => ({ ...f, applicants: f.applicants.filter((_, j) => j !== i) }));
  const hasSpouse = form.applicants.some((p) => p.relationship === 'spouse');

  const pickClient = async (cl) => {
    setError(null);
    if (quote) {
      try {
        await healthsherpa.link(quote.id, cl.id);
        setQuote({ ...quote, client_id: cl.id, client_name: cl.name });
      } catch (e) { setError(detailOf(e)); return; }
    }
    setClient(cl);
    const z = zipFromAddress(cl.address_name);
    if (z && !form.zip) set('zip', z);
  };
  const clearClient = async () => {
    setError(null);
    if (quote && quote.client_id) {
      try { await healthsherpa.unlink(quote.id); setQuote({ ...quote, client_id: null, client_name: null }); } catch (e) { setError(detailOf(e)); return; }
    }
    setClient(null);
  };

  const run = async () => {
    setBusy(true); setError(null);
    try {
      const r = await healthsherpa.createQuote({
        zip: form.zip, fips: form.fips, state: form.state, county_name: form.county_name,
        quote_type: form.quote_type, sep_reason: form.quote_type === 'special_enrollment' ? form.sep_reason : null,
        effective_date: startInfo ? startInfo.effective_date : null, annual_income: form.annual_income,
        applicants: form.applicants.map((p) => ({ relationship: p.relationship, age: p.age === '' ? null : Number(p.age),
          uses_tobacco: p.uses_tobacco, pregnant: p.pregnant, dob: p.dob || null, member_key: p.member_key || null,
          touched: p.touched || [] })),
        income_touched: !!form.income_touched,
        client_id: client ? client.id : null,
        person_id: quotePerson || null,                   // 5.46.0: the quote remembers the HealthSherpa person
      });
      setQuote(r.data);
      setPiResult(r.data.personal_info || null);
    } catch (e) {
      setError(detailOf(e));
      if (/start date/i.test(detailOf(e))) loadDates();     // the date moved on (e.g. midnight): refresh it
    }
    setBusy(false);
  };

  const fmtDay = (iso) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '—');

  return (
    <div style={{ padding: 18 }}>
      <ClientBox c={c} client={client} onSetClient={pickClient} onClear={clearClient} onClientsChanged={onClientsChanged} />
      <div style={{ height: 12 }} />
      <Card c={c} title="Household">
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 160px 180px', gap: 12, alignItems: 'end' }}>
          <div><div style={label(c)}>ZIP code</div><input style={input(c)} value={form.zip} maxLength={5} onChange={(e) => set('zip', e.target.value.replace(/\D/g, ''))} /></div>
          <div><div style={label(c)}>County</div>
            {counties.length > 1 ? (
              <select style={input(c)} value={form.fips} onChange={(e) => { const x = counties.find((k) => k.fips_code === e.target.value); setForm((f) => ({ ...f, fips: x ? x.fips_code : '', state: x ? x.state : '', county_name: x ? x.name : '' })); }}>
                <option value="">Pick the county…</option>
                {counties.map((k) => <option key={k.fips_code} value={k.fips_code}>{k.name} ({k.state})</option>)}
              </select>
            ) : <div style={{ fontSize: fs(14), padding: '8px 0' }}>{form.county_name ? `${form.county_name} (${form.state})` : (countyMsg || '—')}</div>}
          </div>
          <div><div style={label(c)}>Annual household income{form.income_last_year && !form.income_touched ? <span data-testid="hs-quote-income-last" style={{ fontWeight: 400, color: c.textSecondary }}> (last year's — check)</span> : null}</div>
            <input style={input(c)} type="number" min="0" value={form.annual_income} onChange={(e) => setForm((f) => ({ ...f, annual_income: e.target.value, income_touched: true }))} /></div>
          <div><div style={label(c)}>Coverage starts</div>
            <div style={{ fontSize: fs(14), padding: '8px 0', fontWeight: 600 }}>{startInfo ? `${fmtDay(startInfo.effective_date)} (plan year ${startInfo.plan_year})` : '—'}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', marginTop: 12 }}>
          <div style={label(c)}>Quote type *</div>
          {dates && (dates.oe_open ? (
            ['open_enrollment', 'special_enrollment'].map((qt) => (
              <label key={qt} style={{ fontSize: fs(14) }}>
                <input type="radio" name="hs-quote-type" checked={form.quote_type === qt} onChange={() => setForm((f) => ({ ...f, quote_type: qt, sep_reason: qt === 'open_enrollment' ? '' : f.sep_reason }))} />{' '}
                {qt === 'open_enrollment' ? 'Open enrollment' : 'Special enrollment'}
              </label>
            ))
          ) : (
            <span style={{ fontSize: fs(14) }}><strong>Special enrollment</strong> <span style={{ color: c.textSecondary }}>— open enrollment is closed (next window {fmtDay(dates.oe_window.start)} – {fmtDay(dates.oe_window.end)})</span></span>
          ))}
          {form.quote_type === 'special_enrollment' && dates && (
            <select style={input(c, 'auto')} value={form.sep_reason} onChange={(e) => set('sep_reason', e.target.value)} aria-label="Special enrollment reason">
              <option value="">Special enrollment reason *</option>
              {dates.sep_reasons.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
            </select>
          )}
        </div>
        <div style={{ marginTop: 14 }}>
          {prefillNote && <div data-testid="hs-quote-prefill-note" style={{ fontSize: fs(12), color: c.textSecondary, marginBottom: 6 }}>{prefillNote}</div>}
          {form.applicants.map((p, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderTop: i ? `1px solid ${c.borderColor}` : 'none' }}>
              <div style={{ width: 90, fontWeight: 600 }}>{pretty(p.relationship)}{p.label ? <div style={{ fontSize: fs(11), fontWeight: 400, color: c.textSecondary }}>{p.label}</div> : null}</div>
              <label style={{ fontSize: fs(13) }}>Age <input style={input(c, 70)} type="number" min="0" max="120" value={p.age} onChange={(e) => setPerson(i, 'age', e.target.value)} /></label>
              <label style={{ fontSize: fs(13) }} title="Optional — sets the age; saved to the client's personal info (never sent to HealthSherpa)">DOB <input style={input(c, 140)} type="date" value={p.dob || ''} onChange={(e) => setPerson(i, 'dob', e.target.value)} data-testid="hs-quote-dob" /></label>
              <label style={{ fontSize: fs(13) }}><input type="checkbox" checked={p.uses_tobacco} onChange={(e) => setPerson(i, 'uses_tobacco', e.target.checked)} /> Tobacco{(p.carried || []).includes('uses_tobacco') ? <span style={{ color: c.textSecondary }} data-testid="hs-quote-tobacco-last"> (last year's)</span> : null}</label>
              <label style={{ fontSize: fs(13) }}><input type="checkbox" checked={p.pregnant} onChange={(e) => setPerson(i, 'pregnant', e.target.checked)} /> Pregnant</label>
              {p.relationship !== 'primary' && <button style={btn(c)} onClick={() => removePerson(i)}>Remove</button>}
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button style={btn(c, false, hasSpouse)} disabled={hasSpouse} onClick={() => addPerson('spouse')}>+ Spouse</button>
            <button style={btn(c)} onClick={() => addPerson('dependent')}>+ Dependent</button>
            <span style={{ flex: 1 }} />
            <button style={btn(c, true, busy || !form.quote_type || (form.quote_type === 'special_enrollment' && !form.sep_reason))}
              disabled={busy || !form.quote_type || (form.quote_type === 'special_enrollment' && !form.sep_reason)} onClick={run}>{busy ? 'Getting quotes…' : 'Get quotes'}</button>
          </div>
        </div>
        <ErrorBox msg={error} />
        {quotePerson && piResult && <PiCaptureResult c={{ border: c.borderColor, panel: c.panelBg, text: c.textPrimary, sub: c.textSecondary }}
          result={piResult} source="quote"
          valuesFor={(i) => { const p = form.applicants[i] || {}; const t = p.touched || []; const v = {};
            if (p.dob) v.dob = p.dob; if (t.includes('uses_tobacco')) v.tobacco = !!p.uses_tobacco; return v; }} />}
      </Card>
      {quote && <Results c={c} quote={quote} setQuote={setQuote} openClientFolder={openClientFolder} onClientTreesChanged={onClientTreesChanged} footerEl={footerEl} onStartEnrollment={onStartEnrollment} />}
    </div>
  );
}

// --- Quotes history ----------------------------------------------------------------------------

function QuotesHistory({ c, onOpen, onRerun }) {
  const [q, setQ] = useState('');
  const [mine, setMine] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      healthsherpa.listQuotes({ q, mine, limit: 100 }).then((r) => setRows(r.data.quotes || [])).catch((e) => setError(detailOf(e)));
    }, 250);
    return () => clearTimeout(t);
  }, [q, mine]);

  const rerun = async (id) => {
    setBusyId(id); setError(null);
    try { await onRerun(id); } catch (e) { setError(detailOf(e)); }
    setBusyId(null);
  };

  return (
    <div style={{ padding: 18 }}>
      <Card c={c}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input style={input(c, 320)} placeholder="Search by client name…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select style={input(c, 'auto')} value={mine ? 'mine' : 'all'} onChange={(e) => setMine(e.target.value === 'mine')}>
            <option value="all">All users</option><option value="mine">Mine</option>
          </select>
        </div>
        <ErrorBox msg={error} />
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: fs(13), marginTop: 10 }}>
          <thead><tr style={{ textAlign: 'left', color: c.textSecondary }}>
            <th style={{ padding: 6 }}>Date</th><th style={{ padding: 6 }}>Client</th><th style={{ padding: 6 }}>Plan year</th><th style={{ padding: 6 }}>ZIP</th>
            <th style={{ padding: 6 }}>Type</th><th style={{ padding: 6 }}>Plans</th><th style={{ padding: 6 }}>Ticked</th><th style={{ padding: 6 }}>PDF</th><th />
          </tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} style={{ borderTop: `1px solid ${c.borderColor}` }}>
              <td style={{ padding: 6 }}>{fmtDate(r.created_at)}{r.from_cache ? ' (cached)' : ''}</td>
              <td style={{ padding: 6 }}>{r.client_name || <span style={{ color: c.textSecondary }}>Not linked</span>}</td>
              <td style={{ padding: 6 }}>{r.plan_year}</td><td style={{ padding: 6 }}>{r.zip}</td>
              <td style={{ padding: 6 }}>{r.quote_type_label ? `${r.quote_type_label}${r.sep_reason_label ? ` — ${r.sep_reason_label}` : ''}` : <span style={{ color: c.textSecondary }}>—</span>}</td>
              <td style={{ padding: 6 }}>{r.result_count}</td><td style={{ padding: 6 }}>{r.selected_count}</td>
              <td style={{ padding: 6 }}>{r.last_pdf_file_id ? '✓' : ''}</td>
              <td style={{ padding: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>
                <button style={btn(c)} onClick={() => onOpen(r.id)}>Open</button>{' '}
                <button style={btn(c, false, busyId === r.id)} disabled={busyId === r.id} onClick={() => rerun(r.id)} title="Asks HealthSherpa again (a new quote)">{busyId === r.id ? 'Re-running…' : 'Re-run'}</button>
              </td>
            </tr>))}
          </tbody>
        </table>
        {rows.length === 0 && <div style={{ fontSize: fs(12), color: c.textSecondary, padding: 10 }}>No quotes found.</div>}
      </Card>
    </div>
  );
}

// --- Settings (admin) --------------------------------------------------------------------------

// 5.39.0: the self-service text (ASCII / GSM-7; <year> and <link> filled in when sent). The server re-checks it.
function SmsTemplateField({ c, label: text, value, onChange }) {
  const problem = templateProblem(value);
  const sample = smsText(value, new Date().getFullYear() + 1, `https://www.healthsherpa.com/${'x'.repeat(LINK_ESTIMATE - 29)}`);
  const segs = problem ? null : smsSegments(sample);
  return (
    <div style={{ marginBottom: 10 }} data-testid="hs-sms-template">
      <div style={label(c)}>{text}</div>
      <div style={{ fontSize: fs(11), color: c.textSecondary, marginBottom: 3 }}>
        Plain ASCII only. &lt;year&gt; becomes the plan year and &lt;link&gt; the HealthSherpa link (needed once).
      </div>
      <textarea style={{ ...input(c), minHeight: 64, fontFamily: 'inherit' }} value={value} maxLength={320} onChange={(e) => onChange(e.target.value)} aria-label={text} />
      <div style={{ fontSize: fs(12), color: problem ? '#c0392b' : c.textSecondary }} data-testid="hs-sms-segments">
        {problem || `${value.length} characters · about ${segs} text segment${segs === 1 ? '' : 's'} with a ${LINK_ESTIMATE}-character link (the exact count shows when you send)`}
      </div>
    </div>
  );
}

function SettingsTab({ c }) {
  const [vals, setVals] = useState(null);
  const [keyOk, setKeyOk] = useState(null);
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [ping, setPing] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    healthsherpa.getSettings().then((r) => { setVals(r.data.settings); setKeyOk(r.data.key_configured); }).catch((e) => setError(detailOf(e)));
  }, []);

  const save = async () => {
    setBusy(true); setError(null); setMsg(null);
    try { const r = await healthsherpa.putSettings(vals); setVals(r.data.settings); setMsg(r.data.changed.length ? `Saved: ${r.data.changed.join(', ')}` : 'No changes'); } catch (e) { setError(detailOf(e)); }
    setBusy(false);
  };
  const test = async () => {
    setPing('…'); setError(null);
    try { const r = await healthsherpa.ping(); setPing(r.data.ok ? `✓ Connected (${r.data.environment || 'unknown'} environment)` : '✗ Not OK'); } catch (e) { setPing(null); setError(detailOf(e)); }
  };

  if (!vals) return <div style={{ padding: 18 }}><ErrorBox msg={error} />Loading…</div>;
  return (
    <div style={{ padding: 18, maxWidth: 720 }}>
      <Card c={c} title="Connection">
        <div style={{ fontSize: fs(13) }}>API key: {keyOk ? '✓ configured (kept in the server .env, never shown)' : '✗ not configured'}</div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 8 }}>
          <button style={btn(c)} onClick={test}>Test connection</button>
          {ping && <span style={{ fontSize: fs(13) }}>{ping}</span>}
          <span style={{ fontSize: fs(11), color: c.textSecondary }}>(one real HealthSherpa call)</span>
        </div>
      </Card>
      <div style={{ height: 12 }} />
      <Card c={c} title="Agency settings">
        {!String(vals.agent_npn ?? '').replace(/\D/g, '').replace(/^0+/, '') && (
          <div data-testid="hs-settings-aor-notice" style={{ fontSize: fs(13), marginBottom: 10, color: c.textSecondary }}>
            Enter the Agent NPN to turn on agent-of-record checks.
          </div>
        )}
        {SETTING_LABELS.map(([k, l]) => (k === 'enroll_sms_template' ? (
          <SmsTemplateField key={k} c={c} label={l} value={vals[k] ?? ''} onChange={(v) => setVals((x) => ({ ...x, [k]: v }))} />
        ) : k === 'status_tab_staff_visible' ? (
          <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: fs(13) }}>
            <input type="checkbox" checked={String(vals[k]) === 'true'} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.checked ? 'true' : 'false' }))} />
            {l} <span style={{ fontSize: fs(11), color: c.textSecondary }}>(admins always see it; off until launch)</span>
          </label>
        ) : (
          <div key={k} style={{ marginBottom: 10 }}>
            <div style={label(c)}>{l}</div>
            {SETTING_HINTS[k] && <div style={{ fontSize: fs(11), color: c.textSecondary, marginBottom: 3 }} data-testid={`hs-hint-${k}`}>{SETTING_HINTS[k]}</div>}
            <input style={input(c)} value={vals[k] ?? ''} onChange={(e) => setVals((v) => ({ ...v, [k]: e.target.value }))} />
          </div>
        )))}
        <ErrorBox msg={error} />
        {msg && <div style={{ fontSize: fs(13), color: '#2ecc71', marginBottom: 8 }}>{msg}</div>}
        <button style={btn(c, true, busy)} disabled={busy} onClick={save}>Save settings</button>
      </Card>
      <div style={{ height: 12 }} />
      <Card c={c} title="Carrier logos">
        <CarrierLogos c={c} btn={btn} />
      </Card>
      <div style={{ height: 12 }} />
      <Card c={c} title="Follow-up alert types">
        <AlertTypesCard darkMode={c.darkMode} />
      </Card>
      <div style={{ height: 12 }} />
      <Card c={c} title="HealthSherpa in use — lock history">
        <HsLockLogCard c={c} />
      </Card>
      {/* 5.46.0 */}
      <div style={{ height: 12 }} />
      <CmsStatusCard c={c} Card={Card} />
      <div style={{ height: 12 }} />
      <MissingDataCard c={c} Card={Card} />
      <div style={{ height: 12 }} />
      <PiColumnsCard c={c} Card={Card} />
      <div style={{ height: 12 }} />
      <PiViewLogCard c={c} Card={Card} />
    </div>
  );
}

// --- the page ------------------------------------------------------------------------------------

const TAB_IDS = ['dashboard', 'new', 'quotes', 'clients', 'mine', 'followups', 'finalized', 'oe', 'renewal_bonus', 'settings'];   // 5.30.0: + mine, followups, finalized; 5.39.0: + oe; 5.47.0: + renewal_bonus
const urlTab = () => {
  try {
    const t = new URLSearchParams(window.location.search).get('tab');
    return TAB_IDS.includes(t) ? t : 'dashboard';
  } catch (_e) {
    return 'dashboard';
  }
};

const blankForm = () => ({ zip: '', fips: '', state: '', county_name: '', annual_income: '', quote_type: '', sep_reason: '', applicants: [emptyPerson('primary')] });

export default function HealthSherpa({ user, darkMode, onClose, openClientFolder, onClientTreesChanged, onClientsChanged, onOpenWriter }) {
  const [footerEl, setFooterEl] = useState(null);      // 5.16.2: bottom-row slot below the scrolling area
  const c = useColors(darkMode);
  const isAdmin = user?.role === 'admin';
  const [tab, setTabState] = useState(urlTab);           // 5.20.0: from / into the address bar
  const lastClientsUrl = useRef(null);
  const [form, setForm] = useState(blankForm);
  const [quotePerson, setQuotePerson] = useState(null);   // 5.46.0: the HealthSherpa person a quote was started from
  const [quote, setQuote] = useState(null);
  const [client, setClient] = useState(null);
  const [error, setError] = useState(null);
  const [usage, setUsage] = useState(null);            // 5.16.0: admins see a banner at amber / red
  const [staffVisible, setStaffVisible] = useState(false);   // 5.19.0: policy status open to staff?
  const [textScale, setTextScale] = useHsTextScale(user?.id);   // 5.31.0: percent, saved per user on the server
  const [enroll, setEnroll] = useState(null);           // 5.39.0: null = closed; { source } = the dialog is open
  const openEnroll = (source) => setEnroll({ source: source || null, key: Date.now() });

  useEffect(() => {
    if (!isAdmin) return;
    healthsherpa.status().then((r) => setUsage(r.data)).catch(() => {});
  }, [isAdmin]);
  useEffect(() => {
    healthsherpa.getSettings().then((r) => setStaffVisible(String(r.data.settings?.status_tab_staff_visible) === 'true')).catch(() => {});
  }, []);
  const openClient = (id) => { if (openClientFolder) openClientFolder(id); };
  const ui = { Card, ErrorBox, btn, input, label };
  // 5.20.0: tab changes go into the address bar; the Clients tab's own state is kept when you leave it
  useEffect(() => {
    const onPop = () => setTabState(urlTab());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const setTab = (id) => {
    if (id === tab) return;
    if (tab === 'clients') lastClientsUrl.current = readHsUrl();
    if (id === 'clients') writeHsUrl(lastClientsUrl.current || {});
    else {
      try { window.history.pushState(null, '', `${window.location.pathname}?healthsherpa=1&tab=${id}`); } catch (_e) { /* no history */ }
    }
    setTabState(id);
  };
  const openPerson = (id) => { writeHsUrl({ person: String(id) }); setTabState('clients'); };
  // 5.30.0: a client's workspace (My Clients tab) — from the Clients profile panel, Follow-ups and Finalized
  const openWorkspace = (id, season) => {
    if (tab === 'clients') lastClientsUrl.current = readHsUrl();
    writeWorkUrl('mine', { person: String(id), season: season ? String(season) : '' });
    setTabState('mine');
  };
  const showUnassigned = () => { writeHsUrl({ assigned: 'no' }); setTabState('clients'); };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'new', label: 'New Quote' },
    { id: 'quotes', label: 'Quotes' },
    ...(isAdmin || staffVisible ? [{ id: 'clients', label: 'Clients' }, { id: 'mine', label: 'My Clients' },
      { id: 'followups', label: 'Follow-ups' }, { id: 'finalized', label: 'Finalized' },
      { id: 'oe', label: 'Open Enrollment' }, { id: 'renewal_bonus', label: 'Renewal bonus' }] : []),   // 5.30.0; 5.39.0: + Open Enrollment; 5.47.0: + Renewal bonus
    ...(isAdmin ? [{ id: 'settings', label: 'Settings' }] : []),
  ];

  const loadQuote = (q) => {
    setQuote(q);
    setQuotePerson(q.person_id || null);
    setForm({ zip: q.zip, fips: q.fips, state: q.state, county_name: q.county_name || '', annual_income: String(q.annual_income ?? ''),
      quote_type: q.quote_type || '', sep_reason: q.sep_reason || '',
      applicants: (q.household || []).map((p) => ({ relationship: p.relationship, age: String(p.age), uses_tobacco: !!p.uses_tobacco, pregnant: !!p.pregnant })) });
    setClient(q.client_id ? { id: q.client_id, name: q.client_name } : null);
    setTab('new');
  };
  const openQuote = async (id) => {
    setError(null);
    try { const r = await healthsherpa.getQuote(id); loadQuote(r.data); } catch (e) { setError(detailOf(e)); }
  };
  const rerunQuote = async (id) => {
    const r = await healthsherpa.rerunQuote(id);
    loadQuote(r.data);
  };
  const startNew = () => { setQuote(null); setClient(null); setQuotePerson(null); setForm(blankForm()); setTab('new'); };
  // 5.22.0: "Start quote" from the Clients profile panel — the same blank New Quote, with the linked InsureDocs client
  // already picked. The ZIP is NOT prefilled: the county lookup would call HealthSherpa before the user asks for it.
  // 5.46.0: + the person (pid) — the form's people get ages / tobacco from the stored personal info (quotePrefill)
  const startQuoteFor = (cl, pid) => { setQuote(null); setClient(cl ? { id: cl.id, name: cl.full_name || cl.name } : null); setQuotePerson(pid || null); setForm(blankForm()); setTab('new'); };

  return (
    <div className="hs-root" data-testid="hs-root" data-scale={textScale}
      style={{ position: 'fixed', inset: 0, background: c.bg, color: c.textPrimary, zIndex: 900, display: 'flex', flexDirection: 'column',
        '--hs-scale': textScale / 100, fontSize: 'calc(100% * var(--hs-scale, 1))' }}>
      <style>{ROOT_CSS}</style>
      <HsLockStrip showLastUser />{/* 5.44.0 "HealthSherpa in use" (first row of this page); 5.47.0 + the "Last in HealthSherpa" line here only */}
      {/* 5.31.0: min-height + wrap (was height 50) so the bar grows instead of overflowing at 130% */}
      <div style={{ background: c.panelBg, borderBottom: `1px solid ${c.borderColor}`, padding: '6px 18px', display: 'flex', alignItems: 'center', minHeight: 50,
        boxSizing: 'border-box', flexWrap: 'wrap', rowGap: 6, columnGap: 12, flex: 'none' }}>
        <span style={{ fontSize: fs(16), fontWeight: 700, color: '#185FA5' }}>🩺 HealthSherpa</span>
        <div style={{ display: 'flex', borderRadius: 5, overflow: 'hidden', border: `1px solid ${c.borderColor}`, marginLeft: 8 }}>
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '7px 16px', border: 'none', background: tab === t.id ? '#185FA5' : c.panelBg, color: tab === t.id ? '#fff' : c.textPrimary, fontSize: fs(12), fontWeight: 600, cursor: 'pointer' }}>
              {t.label}
            </button>
          ))}
        </div>
        {tab === 'new' && quote && <button style={btn(c)} onClick={startNew}>Start a new quote</button>}
        {/* 5.39.0: on every tab */}
        <button style={btn(c, true)} onClick={() => openEnroll(null)} data-testid="hs-start-enrollment">Start enrollment</button>
        <span style={{ flex: 1 }} />
        {/* 5.16.2: Writer opens from the top on this page (its bottom-left tab is hidden here). The full-screen
            plan view / Compare cover this bar, so the button isn't reachable while they're open. */}
        <TextSizeControl value={textScale} onChange={setTextScale} c={c} />
        {onOpenWriter && <button onClick={onOpenWriter} title="Open Writer (from the top)" style={{ padding: '7px 14px', background: c.panelBg, color: c.textPrimary, border: `1px solid ${c.borderColor}`, borderRadius: 5, fontSize: fs(12), cursor: 'pointer' }}>📝 Writer</button>}
        <button onClick={onClose} style={{ padding: '7px 14px', background: c.panelBg, color: c.textPrimary, border: `1px solid ${c.borderColor}`, borderRadius: 5, fontSize: fs(12), cursor: 'pointer' }}>Close</button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {usage && usage.usage_level !== 'ok' && (
          <div role="status" style={{ background: usage.usage_level === 'red' ? '#A32D2D' : '#b9770e', color: '#fff', padding: '8px 18px', fontSize: fs(13) }}>
            HealthSherpa API: {usage.calls_this_month.toLocaleString()} of {usage.monthly_limit.toLocaleString()} calls used this month{usage.usage_level === 'red' ? ' — very close to the limit' : ''}.
          </div>
        )}
        {error && <div style={{ padding: '0 18px' }}><ErrorBox msg={error} /></div>}
        {tab === 'dashboard' && <Dashboard c={c} isAdmin={isAdmin} onOpenQuote={openQuote} onOpenClient={openClient}
          onOpenPerson={(isAdmin || staffVisible) ? openPerson : null} onGoPolicy={(isAdmin || staffVisible) ? showUnassigned : null} />}
        {tab === 'new' && (
          <NewQuote c={c} form={form} setForm={setForm} quote={quote} setQuote={setQuote} client={client} setClient={setClient}
            openClientFolder={openClientFolder} onClientTreesChanged={onClientTreesChanged} onClientsChanged={onClientsChanged} footerEl={footerEl}
            onStartEnrollment={openEnroll} quotePerson={quotePerson} />
        )}
        {tab === 'quotes' && <QuotesHistory c={c} onOpen={openQuote} onRerun={rerunQuote} />}
        {tab === 'clients' && (isAdmin || staffVisible) && (
          <ClientsTab c={c} ui={ui} isAdmin={isAdmin} openClientFolder={openClientFolder} onClientsChanged={onClientsChanged} NewClientForm={NewClientForm}
            onStartQuote={startQuoteFor} onOpenWorkspace={openWorkspace} />
        )}
        {tab === 'mine' && (isAdmin || staffVisible) && <MyClientsTab darkMode={darkMode} onOpenClient={openClient} />}
        {tab === 'followups' && (isAdmin || staffVisible) && <FollowupsTab darkMode={darkMode} onOpenWorkspace={openWorkspace} />}
        {tab === 'finalized' && (isAdmin || staffVisible) && <FinalizedTab darkMode={darkMode} onOpenWorkspace={openWorkspace} />}
        {tab === 'oe' && (isAdmin || staffVisible) && <OpenEnrollmentTab darkMode={darkMode} onStartEnrollment={openEnroll} />}
        {tab === 'renewal_bonus' && (isAdmin || staffVisible) && <RenewalBonusTab darkMode={darkMode} isAdmin={isAdmin} />}
        {tab === 'settings' && isAdmin && <SettingsTab c={c} />}
      </div>
      {/* 5.16.2: the bottom row's slot — a flex child after the scrolling area; empty (0 height) unless the
          New Quote tab has results */}
      {tab === 'new' && <div ref={setFooterEl} style={{ flex: 'none' }} />}
      <TodayCard />{/* 5.50.0 the Today card (today's items + reminders), as on the main page */}
      {enroll && <EnrollDialog key={enroll.key} darkMode={darkMode} source={enroll.source} onClose={() => setEnroll(null)} />}
    </div>
  );
}
